// SettingsContext — single source of truth for system-wide settings on
// the frontend.  Loads /settings once on mount, exposes a flat lookup
// helper, and provides a refresh() the Settings page calls after saving
// so every consumer (Reservations, CheckInOut, future bill / billing
// flows) updates without a hard reload.
//
// Shape of `settings`:
//   { 'deposit.required': true, 'deposit.min_amount': 200, … }
//
// Reads are guarded by the global auth gate — until the operator is
// logged in we don't fire the request, so the public marketing pages
// and /login don't hit a 401.

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { settingsUrl } from "../BaseURL";

const SettingsContext = createContext({
  settings: {},
  meta: {},        // per-key { type, description, updated_at }
  loading: true,
  refresh: async () => {},
  get: (_key, fallback) => fallback,
});

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState({});
  const [meta, setMeta]         = useState({});
  const [loading, setLoading]   = useState(true);

  const refresh = useCallback(async () => {
    // Skip when no user is cached — the global auth gate would 401 us.
    try {
      const u = JSON.parse(localStorage.getItem("user") || "null");
      if (!u?.id) { setLoading(false); return; }
    } catch (_) {}
    setLoading(true);
    try {
      const res = await axios.get(settingsUrl);
      const map = res.data?.settings || {};
      const values = {}, metaMap = {};
      for (const [k, row] of Object.entries(map)) {
        values[k] = row.value;
        metaMap[k] = { type: row.type, description: row.description, updated_at: row.updated_at };
      }
      setSettings(values);
      setMeta(metaMap);
    } catch (err) {
      // Soft failure — the rest of the app still works on defaults.
      console.error("settings load failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-fetch when localStorage.user changes (login from another tab, etc.).
  useEffect(() => {
    const onStorage = (e) => { if (e.key === "user") refresh(); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refresh]);

  const get = useCallback(
    (key, fallback = null) => (key in settings ? settings[key] : fallback),
    [settings]
  );

  return (
    <SettingsContext.Provider value={{ settings, meta, loading, refresh, get }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}
